<script setup lang="ts">
/* =============================================================
   TOP BAR
   =============================================================
   Left  — drawer toggle (mobile) + the current section, labelled
           from nav.config so it never drifts from the sidebar.
   Right — the signed-in user, their company, and sign out.
   ============================================================= */

import { computed } from 'vue'
import { useRoute } from 'vue-router'
import Icon from '@/components/Icon.vue'
import Button from '@/components/ui/Button.vue'
import UserMenu from './UserMenu.vue'
import { findNavItemByPath } from '@/config/nav.config'
import type { AuthUser } from '@/lib/auth/types'

defineProps<{ user: AuthUser; isMobile: boolean; signingOut: boolean }>()
const emit = defineEmits<{ openDrawer: []; signOut: [] }>()

const route = useRoute()
const current = computed(() => findNavItemByPath(route.path))
</script>

<template>
  <header class="topbar">
    <div class="left">
      <button
        v-if="isMobile"
        type="button"
        class="menuButton"
        aria-label="Open navigation"
        @click="emit('openDrawer')"
      >
        <Icon name="menu" :size="20" />
      </button>

      <span v-if="current" class="section">
        <Icon :name="current.icon" :size="16" class="sectionIcon" />
        {{ current.label }}
      </span>
    </div>

    <div class="right">
      <span class="company" :title="user.company">
        <Icon name="pin" :size="14" />
        {{ user.company }}
      </span>

      <span class="divider" aria-hidden="true" />

      <UserMenu :user="user" @sign-out="emit('signOut')" />

      <Button
        variant="secondary"
        size="sm"
        icon="logout"
        :loading="signingOut"
        :icon-only="isMobile"
        aria-label="Sign out"
        class="signOut"
        @click="emit('signOut')"
      >
        Sign out
      </Button>
    </div>
  </header>
</template>

<style scoped>
.topbar {
  position: sticky;
  top: 0;
  z-index: var(--z-header);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-sm);
  height: var(--topbar-height);
  padding-inline: var(--content-pad-x);
  background-color: color-mix(in srgb, var(--color-surface) 88%, transparent);
  backdrop-filter: blur(var(--blur-header));
  border-bottom: var(--border-width) solid var(--color-border);
}

.left,
.right {
  display: flex;
  align-items: center;
  gap: var(--space-2xs);
  min-width: 0;
}

.menuButton {
  display: grid;
  place-items: center;
  flex: none;
  width: 2.25rem;
  height: 2.25rem;
  color: var(--color-text-muted);
  background: none;
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-sm);
}

.menuButton:hover {
  color: var(--color-heading);
  background-color: var(--color-surface-sunken);
}

.section {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3xs);
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--color-heading);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sectionIcon {
  color: var(--color-text-faint);
}

.company {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3xs);
  max-width: 16rem;
  padding: var(--space-3xs) var(--space-2xs);
  font-size: var(--text-xs);
  font-weight: var(--weight-medium);
  color: var(--color-text-muted);
  background-color: var(--color-surface-sunken);
  border-radius: var(--radius-full);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.company svg {
  flex: none;
  color: var(--color-accent);
}

.divider {
  width: var(--border-width);
  height: 1.5rem;
  background-color: var(--color-border);
}

.signOut {
  flex: none;
}

@media (max-width: 56.25rem) {
  .company,
  .divider {
    display: none;
  }
}
</style>
